import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const MessagingContext = createContext(null);

// Strip the /api path suffix if present — Socket.io connects to the root
const SOCKET_URL = (() => {
  const base = process.env.REACT_APP_API_URL || '';
  return base.replace(/\/api$/, '') || window.location.origin;
})();

let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return _audioCtx;
}

function playNotifSound() {
  try {
    const ctx = getAudioCtx();
    // Browsers suspend AudioContext until a user gesture — resume first
    const play = () => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(1046.5, ctx.currentTime);
      o.frequency.setValueAtTime(1318.5, ctx.currentTime + 0.1);
      g.gain.setValueAtTime(0.2, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + 0.4);
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(play).catch(() => {});
    } else {
      play();
    }
  } catch { /* audio blocked or unavailable */ }
}

export function MessagingProvider({ children }) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState({}); // { [channelId]: Message[] }
  const [typingUsers, setTypingUsers] = useState({}); // { [channelId]: string[] }
  const [latestNotification, setLatestNotification] = useState(null); // { id, channelId, channelName, authorName, body }
  const activeChannelIdRef = useRef(null); // mirrors activeChannelId to avoid stale closures in socket handlers
  const channelsRef = useRef([]); // mirrors channels for socket handler lookups
  const typingTimers = useRef({});

  // Keep refs in sync with state
  useEffect(() => { channelsRef.current = channels; }, [channels]);

  // Load channel list from REST (includes unread counts)
  const fetchChannels = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/messaging/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setChannels(data); // each ch has unreadCount from REST
    } catch { /* ignore */ }
  }, []);

  // Load message history for a channel
  const fetchMessages = useCallback(async (channelId, before = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL || ''}/api/messaging/channels/${channelId}/messages${before ? `?before=${before}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setMessages(prev => ({
        ...prev,
        [channelId]: before ? [...data, ...(prev[channelId] || [])] : data,
      }));
      // Clear unread for this channel after loading (handled by markChannelRead)
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(`${SOCKET_URL}/chat`, {
      path: '/socket.io',
      auth: { token },
      // Polling first, then upgrade to websocket — some networks (cellular, corporate
      // proxies) silently drop the WS upgrade handshake while allowing plain HTTPS
      // through, and listing 'websocket' first leaves the client stuck with no clean
      // fallback. Polling-first degrades gracefully on those networks.
      transports: ['polling', 'websocket'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      fetchChannels();
      // Self-heal: if we were disconnected (or just reconnected) while a channel was
      // open, re-pull its thread in case any messages were missed during the gap.
      if (activeChannelIdRef.current) fetchMessages(activeChannelIdRef.current);
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('message:new', (msg) => {
      setMessages(prev => {
        const existing = prev[msg.channelId] || [];
        if (existing.find(m => m.id === msg.id)) return prev;
        return { ...prev, [msg.channelId]: [...existing, msg] };
      });
      // Increment unread and trigger notification if not the active channel
      if (String(msg.channelId) !== activeChannelIdRef.current) {
        setChannels(prev => prev.map(ch =>
          String(ch.id) === String(msg.channelId)
            ? { ...ch, unreadCount: (ch.unreadCount || 0) + 1 }
            : ch
        ));
        const ch = channelsRef.current.find(c => String(c.id) === String(msg.channelId));
        setLatestNotification({
          id: msg.id,
          channelId: msg.channelId,
          channelName: ch?.name || '',
          authorName: msg.authorName,
          body: msg.body,
        });
        playNotifSound();
      }
    });

    socket.on('message:updated', (msg) => {
      setMessages(prev => {
        const list = prev[msg.channelId] || [];
        return { ...prev, [msg.channelId]: list.map(m => m.id === msg.id ? msg : m) };
      });
    });

    socket.on('message:deleted', ({ messageId, channelId }) => {
      setMessages(prev => {
        const list = prev[channelId] || [];
        return { ...prev, [channelId]: list.filter(m => m.id !== messageId) };
      });
    });

    socket.on('user:typing', ({ channelId, userName, typing }) => {
      setTypingUsers(prev => {
        const list = prev[channelId] || [];
        if (typing) {
          return { ...prev, [channelId]: list.includes(userName) ? list : [...list, userName] };
        } else {
          return { ...prev, [channelId]: list.filter(n => n !== userName) };
        }
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns a promise resolving to { ok, error? } so callers can surface failures
  // instead of the message silently vanishing when the socket is down or the send fails.
  const sendMessage = useCallback((channelId, body) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      return Promise.resolve({ ok: false, error: 'Not connected — check your connection and try again.' });
    }
    return new Promise((resolve) => {
      socket.timeout(8000).emit('message:send', { channelId, body }, (err, response) => {
        if (err) return resolve({ ok: false, error: 'Message timed out — check your connection and try again.' });
        resolve(response || { ok: true });
      });
    });
  }, []);

  const editMessage = useCallback((messageId, body) => {
    socketRef.current?.emit('message:edit', { messageId, body });
  }, []);

  const deleteMessage = useCallback((messageId) => {
    socketRef.current?.emit('message:delete', { messageId });
  }, []);

  const markChannelRead = useCallback((channelId) => {
    socketRef.current?.emit('channel:read', { channelId });
    setChannels(prev => prev.map(ch =>
      String(ch.id) === String(channelId) ? { ...ch, unreadCount: 0 } : ch
    ));
  }, []);

  const sendTyping = useCallback((channelId, isTyping) => {
    socketRef.current?.emit(isTyping ? 'typing:start' : 'typing:stop', { channelId });
  }, []);

  // Throttled typing indicator — call on every keystroke, auto-stops after 2s
  const notifyTyping = useCallback((channelId) => {
    sendTyping(channelId, true);
    clearTimeout(typingTimers.current[channelId]);
    typingTimers.current[channelId] = setTimeout(() => sendTyping(channelId, false), 2000);
  }, [sendTyping]);

  const openChannel = useCallback((channelId) => {
    const id = String(channelId);
    setActiveChannelId(id);
    activeChannelIdRef.current = id;
    fetchMessages(channelId);
    markChannelRead(channelId);
  }, [fetchMessages, markChannelRead]);

  const dismissNotification = useCallback(() => setLatestNotification(null), []);

  const totalUnread = channels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0);

  return (
    <MessagingContext.Provider value={{
      connected, channels, activeChannelId, messages, typingUsers, totalUnread,
      latestNotification, dismissNotification,
      fetchChannels, fetchMessages, openChannel, sendMessage, editMessage, deleteMessage,
      markChannelRead, notifyTyping,
    }}>
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  return useContext(MessagingContext);
}
